// This code is based on the Nuxt 3 loading bar composable

import { computed, getCurrentScope, onScopeDispose, ref } from 'vue';
import type { Ref } from 'vue';

export type LoadingIndicatorOpts = {
  /** @default 2000 */
  duration: number;
  /** @default 200 */
  throttle: number;
  /** @default 500 */
  hideDelay: number;
  /** @default 400 */
  resetDelay: number;
  /**
   * You can provide a custom function to customize the progress estimation,
   * which is a function that receives the duration of the loading bar (above)
   * and the elapsed time. It should return a value between 0 and 100.
   */
  estimatedProgress?: (duration: number, elapsed: number) => number;
};

export type LoadingIndicator = {
  progress: Ref<number>;
  isLoading: Ref<boolean>;
  error: Ref<boolean>;
  start: (opts?: { force?: boolean }) => void;
  set: (value: number, opts?: { force?: boolean }) => void;
  finish: (opts?: { force?: boolean; error?: boolean }) => void;
  clear: () => void;
};

function defaultEstimatedProgress(duration: number, elapsed: number): number {
  const completionPercentage = (elapsed / duration) * 100;
  return (2 / Math.PI) * 100 * Math.atan(completionPercentage / 50);
}

/**
 * Composable to handle the loading state
 */
export function useLoadingIndicator(opts: Partial<LoadingIndicatorOpts> = {}): LoadingIndicator {
  const { duration = 2000, throttle = 200, hideDelay = 500, resetDelay = 400 } = opts;
  const getProgress = opts.estimatedProgress || defaultEstimatedProgress;

  const progress = ref<number>(0);
  const isLoading = ref<boolean>(false);
  const error = ref<boolean>(false);
  let done = false;
  let rafId: number;

  let throttleTimeout: number | NodeJS.Timeout;
  let hideTimeout: number | NodeJS.Timeout;
  let resetTimeout: number | NodeJS.Timeout;

  const start = (opts: { force?: boolean } = {}) => {
    _clearTimeouts();
    error.value = false;
    set(0, opts);
  };

  function set(at = 0, opts: { force?: boolean } = {}) {
    if (at >= 100) {
      return finish({ force: opts.force });
    }
    clear();
    progress.value = at < 0 ? 0 : at;
    const throttleTime = opts.force ? 0 : throttle;

    if (throttleTime) {
      throttleTimeout = setTimeout(() => {
        isLoading.value = true;
        _startProgress();
      }, throttleTime);
    } else {
      isLoading.value = true;
      _startProgress();
    }
  }

  function _hide() {
    hideTimeout = setTimeout(() => {
      isLoading.value = false;
      resetTimeout = setTimeout(() => {
        progress.value = 0;
      }, resetDelay);
    }, hideDelay);
  }

  function finish(opts: { force?: boolean; error?: boolean } = {}) {
    progress.value = 100;
    done = true;
    clear();
    _clearTimeouts();

    if (opts.error) {
      error.value = true;
    }

    if (opts.force) {
      progress.value = 0;
      isLoading.value = false;
    } else {
      _hide();
    }
  }

  function _clearTimeouts() {
    clearTimeout(hideTimeout as number);
    clearTimeout(resetTimeout as number);
  }

  function clear() {
    clearTimeout(throttleTimeout as number);
    cancelAnimationFrame(rafId);
  }

  function _startProgress() {
    done = false;
    let startTimeStamp: number;

    function step(timeStamp: number): void {
      if (done) {
        return;
      }

      startTimeStamp ??= timeStamp;
      const elapsed = timeStamp - startTimeStamp;
      progress.value = Math.max(0, Math.min(100, getProgress(duration, elapsed)));
      rafId = requestAnimationFrame(step);
    }

    rafId = requestAnimationFrame(step);
  }

  // Clean up on component unmount if we're in a scope
  if (getCurrentScope()) {
    onScopeDispose(() => {
      clear();
      _clearTimeouts();
    });
  }

  return {
    progress: computed(() => progress.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    start,
    set,
    finish,
    clear,
  };
}
